
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { AlignmentConfiguration } from "@/types/block-configurations";

interface AlignmentControlsProps {
  value: AlignmentConfiguration;
  onChange: (value: AlignmentConfiguration) => void;
  showVertical?: boolean;
}

/**
 * AlignmentControls Component
 * 
 * Icon-based button group for alignment control.
 * Supports both horizontal and vertical alignment.
 * 
 * @param value - Current alignment configuration
 * @param onChange - Callback when alignment changes
 * @param showVertical - Whether to show vertical alignment controls (default: true)
 */
export function AlignmentControls({ value, onChange, showVertical = true }: AlignmentControlsProps) {
  const updateHorizontal = (horizontal: AlignmentConfiguration['horizontal']) => {
    onChange({ ...value, horizontal });
  };

  const updateVertical = (vertical: AlignmentConfiguration['vertical']) => {
    onChange({ ...value, vertical });
  };

  return (
    <div className="space-y-2.5">
      {/* Horizontal Alignment */}
      <div>
        <Label className="text-xs font-medium mb-1 block">Horizontal</Label>
        <div className="flex gap-1">
          <Button
            type="button"
            size="sm"
            variant={value.horizontal === 'left' ? 'default' : 'outline'}
            onClick={() => updateHorizontal('left')}
            className="flex-1"
            title="Align Left"
          >
            <AlignLeftIcon />
          </Button>
          <Button
            type="button"
            size="sm"
            variant={value.horizontal === 'center' ? 'default' : 'outline'}
            onClick={() => updateHorizontal('center')}
            className="flex-1"
            title="Align Center"
          >
            <AlignCenterIcon />
          </Button>
          <Button
            type="button"
            size="sm"
            variant={value.horizontal === 'right' ? 'default' : 'outline'}
            onClick={() => updateHorizontal('right')}
            className="flex-1"
            title="Align Right"
          >
            <AlignRightIcon />
          </Button>
          <Button
            type="button"
            size="sm"
            variant={value.horizontal === 'justify' ? 'default' : 'outline'}
            onClick={() => updateHorizontal('justify')}
            className="flex-1"
            title="Justify"
          >
            <AlignJustifyIcon />
          </Button>
        </div>
      </div>

      {/* Vertical Alignment */}
      {showVertical && (
        <div>
          <Label className="text-xs font-medium mb-1 block">Vertical</Label>
          <div className="flex gap-1">
            <Button
              type="button"
              size="sm"
              variant={value.vertical === 'top' ? 'default' : 'outline'}
              onClick={() => updateVertical('top')}
              className="flex-1"
              title="Align Top"
            >
              <AlignTopIcon />
            </Button>
            <Button
              type="button"
              size="sm"
              variant={value.vertical === 'middle' ? 'default' : 'outline'}
              onClick={() => updateVertical('middle')}
              className="flex-1"
              title="Align Middle"
            >
              <AlignMiddleIcon />
            </Button>
            <Button
              type="button"
              size="sm"
              variant={value.vertical === 'bottom' ? 'default' : 'outline'}
              onClick={() => updateVertical('bottom')}
              className="flex-1"
              title="Align Bottom"
            >
              <AlignBottomIcon />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// Icon components (simple SVG icons)
function AlignLeftIcon() {
  return (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <line x1="3" y1="5" x2="21" y2="5" strokeWidth={2} strokeLinecap="round" />
      <line x1="3" y1="10" x2="14" y2="10" strokeWidth={2} strokeLinecap="round" />
      <line x1="3" y1="15" x2="18" y2="15" strokeWidth={2} strokeLinecap="round" />
      <line x1="3" y1="20" x2="11" y2="20" strokeWidth={2} strokeLinecap="round" />
    </svg>
  );
}

function AlignCenterIcon() {
  return (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <line x1="3" y1="5" x2="21" y2="5" strokeWidth={2} strokeLinecap="round" />
      <line x1="7" y1="10" x2="17" y2="10" strokeWidth={2} strokeLinecap="round" />
      <line x1="5" y1="15" x2="19" y2="15" strokeWidth={2} strokeLinecap="round" />
      <line x1="8" y1="20" x2="16" y2="20" strokeWidth={2} strokeLinecap="round" />
    </svg>
  );
}

function AlignRightIcon() {
  return (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <line x1="3" y1="5" x2="21" y2="5" strokeWidth={2} strokeLinecap="round" />
      <line x1="10" y1="10" x2="21" y2="10" strokeWidth={2} strokeLinecap="round" />
      <line x1="6" y1="15" x2="21" y2="15" strokeWidth={2} strokeLinecap="round" />
      <line x1="13" y1="20" x2="21" y2="20" strokeWidth={2} strokeLinecap="round" />
    </svg>
  );
}

function AlignJustifyIcon() {
  return (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <line x1="3" y1="5" x2="21" y2="5" strokeWidth={2} strokeLinecap="round" />
      <line x1="3" y1="10" x2="21" y2="10" strokeWidth={2} strokeLinecap="round" />
      <line x1="3" y1="15" x2="21" y2="15" strokeWidth={2} strokeLinecap="round" />
      <line x1="3" y1="20" x2="21" y2="20" strokeWidth={2} strokeLinecap="round" />
    </svg>
  );
}

function AlignTopIcon() {
  return (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <line x1="4" y1="4" x2="4" y2="20" strokeWidth={1.5} strokeLinecap="round" />
      <line x1="20" y1="4" x2="20" y2="20" strokeWidth={1.5} strokeLinecap="round" />
      <line x1="8" y1="6" x2="16" y2="6" strokeWidth={3} strokeLinecap="round" />
    </svg>
  );
}

function AlignMiddleIcon() {
  return (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <line x1="4" y1="4" x2="4" y2="20" strokeWidth={1.5} strokeLinecap="round" />
      <line x1="20" y1="4" x2="20" y2="20" strokeWidth={1.5} strokeLinecap="round" />
      <line x1="8" y1="12" x2="16" y2="12" strokeWidth={3} strokeLinecap="round" />
    </svg>
  );
}

function AlignBottomIcon() {
  return (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <line x1="4" y1="4" x2="4" y2="20" strokeWidth={1.5} strokeLinecap="round" />
      <line x1="20" y1="4" x2="20" y2="20" strokeWidth={1.5} strokeLinecap="round" />
      <line x1="8" y1="18" x2="16" y2="18" strokeWidth={3} strokeLinecap="round" />
    </svg>
  );
}
